import React from 'react';
import { Box } from '@mui/material';

import SecondaryButton from './secondary-button';
import { FONT } from './constants';

interface PaginationBarProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Render even for a single page (hidden by default). */
  alwaysShow?: boolean;
}

// Compact page-number sequence with ellipses, e.g. [1, '…', 4, 5, 6, '…', 20].
const pageSequence = (current: number, totalPages: number): (number | '…')[] => {
  const keep = new Set([1, totalPages, current, current - 1, current + 1]);
  const sorted = Array.from(keep)
    .filter((n) => n >= 1 && n <= totalPages)
    .sort((a, b) => a - b);
  const out: (number | '…')[] = [];
  let prev = 0;
  sorted.forEach((n) => {
    if (n - prev > 1) out.push('…');
    out.push(n);
    prev = n;
  });
  return out;
};

interface PageButtonProps {
  children: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  nav?: boolean;
  ariaLabel?: string;
  ariaCurrent?: boolean;
  onClick: () => void;
}

// Reuses the shared secondary-button style for inactive pages / arrows; the active
// page is highlighted with a purple outline so the current page stays obvious.
const PageButton: React.FC<PageButtonProps> = ({
  children,
  active = false,
  disabled = false,
  nav = false,
  ariaLabel,
  ariaCurrent,
  onClick,
}) => (
  <SecondaryButton
    variant="text"
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    aria-current={ariaCurrent ? 'page' : undefined}
    sx={{
      minWidth: 34,
      height: 34,
      padding: '0 9px',
      borderRadius: '6px',
      fontSize: FONT.base,
      fontWeight: 500,
      lineHeight: nav ? 1 : undefined,
      ...(active && {
        border: '1px solid #7801ef',
        color: '#7801ef',
        cursor: 'default',
        '&:hover': { backgroundColor: 'transparent' },
      }),
    }}
  >
    {children}
  </SecondaryButton>
);

const PaginationBar: React.FC<PaginationBarProps> = ({
  page,
  totalPages,
  onPageChange,
  alwaysShow = false,
}) => {
  if (totalPages <= 1 && !alwaysShow) return null;
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexWrap="wrap"
      gap="4px"
      padding="16px 20px 20px"
      borderTop="1px solid #e2e8f0"
    >
      <PageButton
        nav
        disabled={page <= 1}
        ariaLabel="Previous page"
        onClick={() => onPageChange(page - 1)}
      >
        ‹
      </PageButton>

      {pageSequence(page, totalPages).map((n, i) =>
        n === '…' ? (
          // eslint-disable-next-line react/no-array-index-key
          <Box
            key={`ellipsis-${i}`}
            component="span"
            minWidth={22}
            textAlign="center"
            fontSize={FONT.sm}
            color="#94a3b8"
          >
            …
          </Box>
        ) : (
          <PageButton
            key={n}
            active={n === page}
            ariaCurrent={n === page}
            onClick={() => onPageChange(n)}
          >
            {n}
          </PageButton>
        )
      )}

      <PageButton
        nav
        disabled={page >= totalPages}
        ariaLabel="Next page"
        onClick={() => onPageChange(page + 1)}
      >
        ›
      </PageButton>
    </Box>
  );
};

export default PaginationBar;
